import * as React from 'react'
import { getOrMakeListener } from './listeners'

export function addDomEvent(
  target: EventTarget | Window,
  eventName: string,
  handler: EventListener,
  options?: AddEventListenerOptions,
) {
  return getOrMakeListener(target, eventName, handler, options)
}

export function loadImage(src?: string | void | null) {
  if (!src) {
    return Promise.reject('No src provided')
  }

  // oxlint-disable-next-line promise/avoid-new
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.setAttribute('crossorigin', 'anonymous')
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      resolve(img)
    }
    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }
    img.src = src
  })
}

/** Loads an image that may have been reused from cache, so apply CORS fix */
export async function decodeReusedImage(src?: string | void | null) {
  if (!src) {
    throw new Error('No src provided')
  }
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = getCorsFixedUrl(src)
  await img.decode()
  return img
}

// loadImage does not load svg properly in firefox as width and height stay at 0.
// oxlint-disable-next-line require-await
export async function loadSVG(src?: string) {
  if (!src) {
    return Promise.reject('No src provided')
  }

  return fetch(src)
    .then(response => response.text())
    .then(svgText => {
      const parser = new DOMParser()
      const svgDoc = parser.parseFromString(svgText, 'image/svg+xml')
      const svgElement = svgDoc.querySelector('svg')
      if (!svgElement) {
        throw new Error('Invalid SVG')
      }

      // get width and height here or fallback to viewbox
      let width: string | number | null = svgElement.getAttribute('width')
      let height: string | number | null = svgElement.getAttribute('height')
      const viewBox = svgElement.getAttribute('viewBox')

      if ((!width || !height) && viewBox) {
        const viewBoxDimensions = viewBox.split(' ').map(Number)
        width = width || viewBoxDimensions?.[2]
        height = height || viewBoxDimensions?.[3]
      }

      const widthNumber = typeof width === 'string' ? parseFloat(width) : width
      const heightNumber = typeof height === 'string' ? parseFloat(height) : height

      const img = new Image()
      img.src = src
      return { img, dimensions: { width: widthNumber || 0, height: heightNumber || 0 } }
    })
    .catch(error => {
      throw error
    })
}

/** Adds a dummy fix query parameter to the URL to avoid previous cached images
 * by the browsers with originally different cross origin images to fail (known
 * browsers wontfix issue) */
export function getCorsFixedUrl(url: string) {
  return url.includes('?') ? `${url}&__corsfix=1` : `${url}?__corsfix=1`
}

export function imageToBase64(img: HTMLImageElement) {
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height

  const ctx = canvas.getContext('2d')

  ctx?.drawImage(img, 0, 0)

  return canvas.toDataURL()
}

export function base64ToFile(dataurl: string, filename: string) {
  const arr = dataurl.split(',')
  const mime = arr[0].match(/:(.*?);/)![1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    // oxlint-disable-next-line unicorn/prefer-code-point
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new File([u8arr], filename, { type: mime })
}

export async function imageUrlToBase64(url: string): Promise<string> {
  const data = await fetch(url)
  const blob = await data.blob()
  // oxlint-disable-next-line promise/avoid-new
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(blob)
    reader.onloadend = () => {
      const base64data = reader.result
      resolve(base64data as string)
    }
    reader.onerror = reject
  })
}

export const getHasEyeDropperSupport = () => 'EyeDropper' in window

/**
 * Deletes a browser cookie by name.
 *
 * @param name - The name of the cookie to delete.
 * @param path - Optional. The path the cookie was set on (default is "/").
 * @param domain - Optional. The domain the cookie was set on.
 */
export function deleteCookie(name: string, path = '/', domain?: string): void {
  let cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`
  if (domain) {
    cookie += ` domain=${domain};`
  }
  document.cookie = cookie
}

export function setCookie({
  name,
  value,
  expiresIn,
  domain,
  path = '/',
}: {
  name: string
  value: string
  expiresIn?: number
  domain?: string
  path?: string
}) {
  let expires = ''
  if (expiresIn) {
    const date = new Date()
    date.setTime(date.getTime() + expiresIn)
    expires = '; expires=' + date.toUTCString()
  }
  let domainString = ''
  if (domain) {
    domainString = ';domain=' + domain
  }
  let pathString = ''
  if (path) {
    pathString = ';path=' + path
  }
  document.cookie = name + '=' + (value || '') + expires + domainString + pathString + ';'
}

export function getCookie(cname: string) {
  if (typeof document === 'undefined') {
    return ''
  }

  const name = cname + '='
  const decodedCookie = decodeURIComponent(document.cookie)
  const ca = decodedCookie.split(';')

  // oxlint-disable-next-line typescript/prefer-for-of
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') {
      c = c.substring(1)
    }

    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length)
    }
  }

  return ''
}

export function mergeFormData(...formStates: (FormData | null)[]) {
  const form = new FormData()

  // oxlint-disable-next-line unicorn/no-array-reverse
  for (const formState of formStates.reverse()) {
    if (!formState) {
      continue
    }

    // oxlint-disable-next-line unicorn/prefer-spread
    for (const [key, value] of Array.from(formState.entries())) {
      if (form.has(key)) {
        continue
      }

      form.append(key, value)
    }
  }

  return form
}

export function sleep(ms: number) {
  // oxlint-disable-next-line promise/avoid-new
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * The DOM likes values to be fixed to 3 decimal places
 */
export function toDomPrecision(v: number) {
  return +v.toFixed(4)
}

export function nextFrame(callback: FrameRequestCallback) {
  return requestAnimationFrame(() => requestAnimationFrame(callback))
}

export function isInEditElement() {
  return (
    document.activeElement?.tagName === 'INPUT' ||
    document.activeElement?.tagName === 'TEXTAREA' ||
    (document.activeElement as HTMLDivElement)?.contentEditable === 'true'
  )
}

/**
 * Sometimes an SVG can have no width and no height.
 * This function fixes that _if_ the SVG element has a viewBox. Otherwise
 * we can't really do anything else as it's maybe impossible but definitely
 * hard to know the size of the SVG, and the person who initially
 * wrote this function (me) doesn't know how to do that.
 *
 * So, if it doesn't have a viewBox, then it does nothing.
 *
 * I am not sure why TypeScript doesn't like `viewBox`,
 * although the specs say it's there on SVGElements.
 * see https://developer.mozilla.org/en-US/docs/Web/API/SVGMarkerElement/viewBox
 *
 * @param svg svg string
 * @returns fixed svg
 */
export function addWidthAndHeightToSvgFromViewbox(svg: string) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(svg, 'image/svg+xml')
  const svgElement =
    doc.documentElement instanceof SVGElement ? doc.documentElement : findSvgElement(doc)

  // oxlint-disable-next-line radix
  const width = parseInt(svgElement.getAttribute('width') ?? '')
  // oxlint-disable-next-line radix
  const height = parseInt(svgElement.getAttribute('height') ?? '')

  if (width && height) {
    return svg
  }

  const svgElementViewBox = svgElement as unknown as SVGFitToViewBox
  const viewBox = svgElementViewBox?.viewBox?.baseVal
  if (viewBox) {
    svgElement.setAttribute('width', `${viewBox.width}`)
    svgElement.setAttribute('height', `${viewBox.height}`)
  }

  return svgElement.outerHTML
}

function findSvgElement(doc: Document) {
  const svgElement = doc.querySelector('svg')
  if (!svgElement) {
    throw new Error('Could not find SVG element')
  }
  return svgElement
}

export function requestIdleCallbackPonyfill(callback: IdleRequestCallback) {
  try {
    // eslint-disable-next-line no-restricted-globals
    return requestIdleCallback(callback)
  } catch {}

  setTimeout(callback, 0)
}

/**
 * Runs the callback only after the window `load` event AND a subsequent idle period.
 * Unlike requestIdleCallbackPonyfill alone (which can fire while critical resources
 * are still downloading), this guarantees deferred work never competes with the
 * initial page load — use it to start non-critical media/scripts.
 *
 * Returns a cancel function.
 */
export function runAfterWindowLoadIdle(callback: () => void) {
  let cancelled = false

  const runOnIdle = () => {
    requestIdleCallbackPonyfill(() => {
      if (!cancelled) {
        callback()
      }
    })
  }

  if (document.readyState === 'complete') {
    runOnIdle()
  } else {
    window.addEventListener('load', runOnIdle, { once: true })
  }

  return () => {
    cancelled = true
    window.removeEventListener('load', runOnIdle)
  }
}

export function getHtmlCharacterCount(input: string): number {
  const regex = /(<([^>]+)>)/gi
  const result = input.replace(regex, '')

  return result.length
}

export function scrollToElementWithDataAttribute({
  dataQuery,
  containerDataQuery,
  offset,
}: {
  dataQuery: string
  containerDataQuery?: string
  offset?: number
}) {
  const element = document.querySelector(dataQuery)
  const container = containerDataQuery ? document.querySelector(containerDataQuery) : window

  if (element) {
    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition - (offset ?? 0)

    container?.scrollBy({
      top: offsetPosition,
      behavior: 'smooth',
    })
  }
}

export function isUrlEncoded(str: string) {
  try {
    const decodedStr = decodeURIComponent(str)
    return decodedStr !== str
    // oxlint-disable-next-line unicorn/catch-error-name
  } catch (e) {
    return false
  }
}

export function load(src: string) {
  if (document.querySelectorAll(`[href='${src}']`).length > 0) {
    return
  }

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = src
  document.head.appendChild(link)
}

export function asyncLoad(src: string) {
  //In case we have multiple elements of the same type check if the styles are already loaded
  if (document.querySelectorAll(`[href='${src}']`).length > 0) {
    return Promise.resolve()
  }
  // oxlint-disable-next-line promise/avoid-new
  return new Promise<void>((resolve, reject) => {
    const link = document.createElement('link')
    link.type = 'text/css'
    link.rel = 'stylesheet'
    link.href = src
    link.onload = () => {
      resolve()
    }
    link.onerror = () => {
      reject()
    }

    document.head.appendChild(link)
  })
}

export function distanceToRect(x: number, y: number, rect: DOMRect) {
  const dx = Math.max(rect.left - x, 0, x - rect.right)
  const dy = Math.max(rect.top - y, 0, y - rect.bottom)
  return Math.hypot(dx, dy)
}

export function reactNodeToString(node: React.ReactNode): string {
  if (typeof node === 'string') {
    return node
  }

  if (typeof node === 'number') {
    return String(node)
  }

  if (React.isValidElement(node)) {
    return reactNodeToString((node.props as { children?: React.ReactNode }).children)
  }

  if (Array.isArray(node)) {
    return node.map(reactNodeToString).join('')
  }

  return ''
}

export function getWidthAndHeightOfImage(img: HTMLImageElement) {
  const attrWidth = img.getAttribute('width')
  const attrHeight = img.getAttribute('height')
  return {
    width: img.naturalWidth || (attrWidth ? parseFloat(attrWidth) : img.width),
    height: img.naturalHeight || (attrHeight ? parseFloat(attrHeight) : img.height),
  }
}

/**
 * Opens a URL in a new tab with a deferred execution.
 *
 * Using setTimeout(fn, 0) ensures the browser doesn't block the popup
 * when called from within async callbacks (e.g. mutation onSuccess handlers).
 * Without this, some browsers may treat the window.open as not user-initiated
 * and block the popup.
 */
export function openLink(url: string): void {
  setTimeout(() => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }, 0)
}

/**
 * Waits for an `<img>` element to be ready to render.
 *
 * Important: image `error` events are not replayed. If an image already failed to load
 * *before* this function is called, then `img.complete === true` and `naturalWidth/Height === 0`,
 * and waiting for `load/error` events would hang indefinitely. In that case we reject immediately.
 */
export async function awaitImageElementLoad(img: HTMLImageElement): Promise<void> {
  if (img.complete) {
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      return
    }
    // treat it as a no-op (nothing to load)
    if (!img.currentSrc && !img.src) {
      return
    }
    throw new Error('Image failed to load')
  }

  // prefer decode() when available; it resolves once the image is decoded and ready to render.
  // some edge cases can reject (e.g. broken image), so fall back to event-based loading.
  try {
    if (typeof img.decode === 'function') {
      await img.decode()
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        return
      }
    }
  } catch {}

  // oxlint-disable-next-line promise/avoid-new
  await new Promise<void>((resolve, reject) => {
    const onLoad = () => resolve()
    const onError = () => reject(new Error('Image failed to load'))

    img.addEventListener('load', onLoad, { once: true })
    img.addEventListener('error', onError, { once: true })
  })
}
